/* global setTimeout, clearTimeout */
import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, DeviceEventEmitter, Modal, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Icon from 'react-native-vector-icons/Feather';
import { priceFormatter } from '../helpers/numbers';

export const NPIPE_PAYMENT_STATUS_EVENT = 'npipePaymentStatus';

const FINAL_PHASES = ['approved', 'declined', 'timeout', 'cancelled', 'error'];

/**
 * Publishes the progress of a Novo (NPIPE) card payment to every mounted NpipePaymentDialog.
 * Pass this as `onStatus` to payWithNpipe. Phases: 'waiting' (spinner + abort), 'approved',
 * 'declined', 'timeout', 'cancelled', 'error' (message + OK), 'idle' (hide).
 *
 * @param {{ phase: string, amount?: number, message?: string, onCancel?: Function }} status
 */
export const emitNpipeStatus = (status) => {
    DeviceEventEmitter.emit(NPIPE_PAYMENT_STATUS_EVENT, status || { phase: 'idle' });
};

/**
 * Builds the `onStatus` callback for payWithNpipe (it reports 'waiting_for_terminal' | 'printing' | 'done').
 *
 * @param {number} amount amount shown in the dialog (major units)
 * @returns {Function} onStatus callback
 */
export const createNpipeStatusHandler = (amount) => (state) => {
    if (state === 'waiting_for_terminal') {
        emitNpipeStatus({ phase: 'waiting', amount });
    } else if (state === 'printing') {
        emitNpipeStatus({ phase: 'printing', amount });
    }
};

/**
 * Shows the final outcome (approved / declined / timeout / cancelled / error) of a payWithNpipe result.
 *
 * @param {Object} payResult value returned by payWithNpipe
 * @param {number} amount amount shown in the dialog (major units)
 */
export const showNpipeOutcome = (payResult, amount) => {
    const r = payResult || {};
    if (r.approved) {
        emitNpipeStatus({ phase: 'approved', amount });
        return;
    }
    if (r.cancelled) {
        emitNpipeStatus({ phase: 'cancelled', amount });
        return;
    }
    const err = r.error || {};
    let phase = 'error';
    if (err.unknownOutcome || /TIMEOUT/i.test(String(err.code || ''))) {
        phase = 'timeout';
    } else if (err.kind === 'declined') {
        phase = 'declined';
    }
    emitNpipeStatus({ phase, amount, message: phase === 'timeout' ? undefined : err.message });
};

/**
 * Progress dialog for card payments on the Novo terminal (NPIPE): waiting for the card,
 * abort while waiting, and a clear approved / declined / timeout result.
 */
const NpipePaymentDialog = () => {
    const { t } = useTranslation();
    const [status, setStatus] = useState(null);
    const hideTimer = useRef(null);

    useEffect(() => {
        const subscription = DeviceEventEmitter.addListener(NPIPE_PAYMENT_STATUS_EVENT, (data) => {
            if (hideTimer.current) {
                clearTimeout(hideTimer.current);
                hideTimer.current = null;
            }
            if (!data || data.phase === 'idle') {
                setStatus(null);
                return;
            }
            setStatus(data);
            if (data.phase === 'approved') {
                hideTimer.current = setTimeout(() => setStatus(null), 1500);
            }
        });
        return () => {
            subscription.remove();
            if (hideTimer.current) {
                clearTimeout(hideTimer.current);
            }
        };
    }, []);

    if (!status) {
        return null;
    }

    const phase = status.phase;
    const isFinal = FINAL_PHASES.includes(phase);
    const waiting = !isFinal;
    const canAbort = phase === 'waiting';

    const handleAbort = async () => {
        try {
            if (typeof status.onCancel === 'function') {
                await status.onCancel();
            } else {
                const { abort } = require('../paymentModules/npipe/npipeClient');
                await abort();
            }
        } catch (error) {
            console.warn('[NpipePaymentDialog] abort failed', error);
        }
    };

    const titles = {
        printing: t('npipe_printing_receipt', { defaultValue: 'Printing receipt…' }),
        waiting: t('npipe_waiting_for_card', { defaultValue: 'Waiting for card on terminal…' }),
        approved: t('npipe_payment_approved', { defaultValue: 'Payment approved' }),
        declined: t('npipe_payment_declined', { defaultValue: 'Payment declined' }),
        timeout: t('npipe_payment_timeout', { defaultValue: 'No answer from the terminal (timeout). Check the terminal before retrying – do not charge twice.' }),
        cancelled: t('npipe_payment_cancelled', { defaultValue: 'Payment cancelled' }),
        error: t('npipe_payment_error', { defaultValue: 'Card payment failed' }),
    };
    const iconName = phase === 'approved' ? 'check-circle' : (isFinal ? 'x-circle' : 'credit-card');
    const iconColor = phase === 'approved' ? '#2e9e4f' : (isFinal ? '#d9534f' : '#F76E24');

    return (
        <Modal visible transparent animationType="fade" onRequestClose={isFinal ? () => setStatus(null) : (canAbort ? handleAbort : undefined)}>
            <View style={styles.modalContainer}>
                <View style={styles.modalContent}>
                    <Icon name={iconName} size={50} color={iconColor} style={styles.icon} />
                    <Text style={styles.title}>{titles[phase] || titles.waiting}</Text>
                    {status.amount != null && (
                        <Text style={styles.text}>{priceFormatter(status.amount)}</Text>
                    )}
                    {!!status.message && <Text style={styles.text}>{status.message}</Text>}
                    {waiting && <ActivityIndicator size="large" color="#F76E24" style={styles.icon} />}
                    {canAbort && (
                        <Text style={styles.hint}>
                            {t('npipe_follow_terminal', { defaultValue: 'Follow the instructions on the terminal.' })}
                        </Text>
                    )}
                    <View style={styles.buttonContainer}>
                        {canAbort ? (
                            <TouchableOpacity style={styles.cancelButton} onPress={handleAbort}>
                                <Text style={styles.cancelButtonText}>
                                    {t('npipe_abort_payment', { defaultValue: 'Abort payment' })}
                                </Text>
                            </TouchableOpacity>
                        ) : isFinal ? (
                            <TouchableOpacity style={styles.okButton} onPress={() => setStatus(null)}>
                                <Text style={styles.okButtonText}>{t('npipe_ok', { defaultValue: 'OK' })}</Text>
                            </TouchableOpacity>
                        ) : null}
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    modalContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    modalContent: {
        backgroundColor: 'white',
        borderRadius: 10,
        padding: 20,
        width: '80%',
        maxWidth: 560,
        maxWidth: 400,
        alignItems: 'center',
    },
    icon: { marginBottom: 16 },
    title: { fontSize: 20, fontWeight: 'bold', marginBottom: 10, textAlign: 'center', color: '#222' },
    text: { fontSize: 16, marginBottom: 12, textAlign: 'center', color: '#222' },
    hint: { fontSize: 14, marginBottom: 16, textAlign: 'center', color: '#666' },
    buttonContainer: { flexDirection: 'row', justifyContent: 'center', width: '100%' },
    cancelButton: { backgroundColor: '#ccc', padding: 12, borderRadius: 5, flex: 1, alignItems: 'center' },
    cancelButtonText: { color: '#333', fontWeight: 'bold' },
    okButton: { backgroundColor: '#F76E24', padding: 12, borderRadius: 5, flex: 1, alignItems: 'center' },
    okButtonText: { color: 'white', fontWeight: 'bold' },
});

export default NpipePaymentDialog;
